import React, { useCallback, useState } from 'react';
import {
  AppButton,
  Box,
  FormProvider,
  getValidationResolver,
  RawButton,
  SubmitHandler,
  Text,
  useForm,
} from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import { Modal } from '@/components';
import { PendingModal } from '@/components/elements';
import {
  FieldLevelAccessSetupProperties,
  IFieldLevelAccessProperties,
  IPropertiesSection,
} from '@/modules/role/role.types';

import { useFieldLevelAccessSetupStore } from '../../stores';

import SelectAccessLevelProperties from './SelectAccessLevelProperties';

interface Props {
  onApply: (values: FieldLevelAccessSetupProperties) => void;
}
const PropertiesCopyModal: React.FC<Props> = ({ onApply }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [properties, setProperties] = useState<IFieldLevelAccessProperties>({});
  const [overridableProperties, setOverridableProperties] = useState<IFieldLevelAccessProperties>({});
  const methods = useForm<IPropertiesSection>({
    defaultValues: {
      isCopyLevelAccess: true,
    },
    resolver: getValidationResolver({
      copyRoleId: VALIDATORS.STRING.when('isCopyRole', {
        is: (val?: boolean) => {
          return val === true;
        },
        then: () => VALIDATORS.REQUIRED_STRING,
        otherwise: (schema) => schema,
      }),
    }),
  });

  const setState = useFieldLevelAccessSetupStore((state) => state.setState);
  const handleSubmit: SubmitHandler<IPropertiesSection> = useCallback(() => {
    setIsConfirm(true);
  }, []);
  const handleConfirmClose = useCallback(() => {
    setIsConfirm(false);
  }, []);
  const handleConfirm = useCallback(() => {
    setIsVisible(false);
    setIsConfirm(false);
    setState({
      properties,
      overridableProperties,
    });

    onApply({
      properties,
      overridableProperties,
    });

    methods.reset();
  }, [methods, onApply, properties, overridableProperties, setState]);

  return (
    <>
      <Modal testId="permission-copy" visible={isVisible} onSetVisible={setIsVisible}>
        <PendingModal
          visible={isConfirm}
          onSetVisible={setIsConfirm}
          header="Override existing properties?"
          helptext=""
        >
          <Box direction="row" align="center">
            <AppButton width="180px" onClick={handleConfirmClose} buttonType="secondary">
              Cancel
            </AppButton>
            <Box width="s" />
            <AppButton width="180px" buttonType="primary" onClick={handleConfirm} color="red">
              Yes, Override
            </AppButton>
          </Box>
        </PendingModal>
        <FormProvider {...methods}>
          <form name="permissionsCopy" onSubmit={methods.handleSubmit(handleSubmit)}>
            <Box>
              <Text textAlign="center" size="2xl" weight={700} margin={{ bottom: 'xs' }}>
                Copy Properties
              </Text>
              <Text textAlign="center">
                Quickly create new field level access entities by copying properties from an existing one. Any
                permissions you set manually will take precedence over the template's settings.
              </Text>
            </Box>
            <Box round="moduleRound" background="module" pad="l" margin={{ top: 'spacing24' }}>
              <SelectAccessLevelProperties
                onChangeOverridableProperties={setOverridableProperties}
                onChangeProperties={setProperties}
              />
            </Box>
            <Box direction="row" justify="end" margin={{ top: 'l' }}>
              <AppButton width="160px" onClick={() => setIsVisible(false)} buttonType="secondary">
                Cancel
              </AppButton>
              <Box margin={{ left: 's' }}>
                <AppButton width="160px" type="submit">
                  Apply (1/2)
                </AppButton>
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Modal>
      <Box width={{ min: '120px' }}>
        <RawButton onClick={() => setIsVisible(true)}>
          <Text weight={400} color="textAccent">
            Copy Properties
          </Text>
        </RawButton>
      </Box>
    </>
  );
};

export default PropertiesCopyModal;
